import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  shouldShowTutorial,
  isFirstTutorialInSession,
  markFirstTutorialShown,
  deferTutorialForSession,
  setTutorialPreference,
  markTutorialWatched,
  incrementFeatureUsage,
  syncTutorialDataFromBackend,
  markFeaturePromptShown,
} from '../utilis/tutorial-storage';

const TutorialContext = createContext(null);

/**
 * Tutorial Provider
 * Manages tutorial state and provides methods to trigger tutorials
 */
export const TutorialProvider = ({ children, isAuthenticated }) => {
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [pendingTutorials, setPendingTutorials] = useState([]);
  const [isFirstInSession, setIsFirstInSession] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  // Sync tutorial data from backend on mount
  useEffect(() => {
    const syncData = async () => {
      if (!isAuthenticated) {
        console.log('Not authenticated, skipping tutorial sync');
        return;
      }
      setIsSyncing(true);
      try {
        await syncTutorialDataFromBackend();
      } catch (error) {
        console.error('Error syncing tutorial data:', error);
      } finally {
        setIsSyncing(false);
      }
    };
    syncData();
    setIsFirstInSession(isFirstTutorialInSession());
  }, [isAuthenticated]);

  /**
   * Check if a tutorial should be shown and trigger it
   * @param {string} featureId - The feature identifier
   * @param {object} tutorialData - Tutorial configuration (video, title, description, nextFeature)
   */
  const checkAndShowTutorial = useCallback(async (featureId, tutorialData) => {
    const shouldShow = await shouldShowTutorial(featureId);
    if (!shouldShow) {
      return false;
    }

    const tutorial = {
      featureId,
      ...tutorialData
    };

    // Show full prompt only if this is the first feature in the session
    // All subsequent features in the same session will show the tiny bar
    if (isFirstInSession) {
      setActiveTutorial(tutorial);
      markFeaturePromptShown(featureId);
      markFirstTutorialShown();
      setIsFirstInSession(false);
      return true;
    }

    // Subsequent features in the same session → show tiny bar
    setPendingTutorials(prev => {
      if (prev.find(t => t.featureId === featureId)) {
        return prev;
      }
      return [...prev, tutorial];
    });
    return true;
  }, [isFirstInSession]);

  /**
   * Remove tutorial from pending list
   */
  const removePendingTutorial = useCallback((featureId) => {
    setPendingTutorials(prev => prev.filter(t => t.featureId !== featureId));
  }, []);

  /**
   * Handle tutorial action
   */
  const handleTutorialAction = useCallback(async (featureId, action) => {
    switch (action) {
      case 'play':
        // Tutorial will be played by the player component
        break;
      case 'remind_later':
        // Only defer for this session - don't save to backend
        // This allows it to ask again next session
        deferTutorialForSession(featureId);
        setActiveTutorial(null);
        removePendingTutorial(featureId);
        break;
      case 'dont_show':
        // Disable tutorials globally for ALL features
        // Get all tutorial features and set 'dont_show' for each
        const { getAllTutorials } = await import('../config/tutorials.config');
        const allTutorials = getAllTutorials();
        
        // Set 'dont_show' preference for all tutorial features
        await Promise.all(
          allTutorials.map(tutorial => 
            setTutorialPreference(tutorial.featureId, 'dont_show')
          )
        );
        
        setActiveTutorial(null);
        removePendingTutorial(featureId);
        break;
      case 'watched':
        await markTutorialWatched(featureId);
        setActiveTutorial(null);
        removePendingTutorial(featureId);
        break;
      default:
        break;
    }
  }, [removePendingTutorial]);

  /**
   * Show tutorial from pending list
   */
  const showPendingTutorial = useCallback((featureId) => {
    const tutorial = pendingTutorials.find(t => t.featureId === featureId);
    if (tutorial) {
      setActiveTutorial(tutorial);
      removePendingTutorial(featureId);
    }
  }, [pendingTutorials, removePendingTutorial]);

  /**
   * Close tutorial
   */
  const closeTutorial = useCallback(() => {
    setActiveTutorial(null);
  }, []);

  /**
   * Track feature usage (call when user successfully uses a feature)
   */
  const trackFeatureUsage = useCallback(async (featureId) => {
    await incrementFeatureUsage(featureId);
  }, []);

  const value = {
    activeTutorial,
    pendingTutorials,
    isFirstInSession,
    isSyncing,
    checkAndShowTutorial,
    handleTutorialAction,
    showPendingTutorial,
    closeTutorial,
    removePendingTutorial,
    trackFeatureUsage
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

/**
 * Hook to use tutorial context
 */
export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

export default TutorialContext;

